if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();

      this.button = this.querySelector('.load-more');
      this.grid = document.querySelector(
        `[data-id="${CSS.escape(this.dataset.section)}"]`
      );

      this.animations_enabled =
        document.body.classList.contains('animations-true') &&
        typeof gsap !== 'undefined';

      const currentPage =
        Number(new URL(location.href).searchParams.get('page')) || 1;

      this.nextPage = currentPage + 1;
      this.loadedUrls = [];
      this.restoring = false;
      this.exhausted = false;

      // 包含系列路径、筛选、排序、语言路径和 section。
      this.storageKey =
        'collection-pagination:v1:' +
        location.pathname +
        location.search +
        ':' +
        this.dataset.section;
    }

    async connectedCallback() {
      if (!this.grid || this.initialized) return;
      this.initialized = true;

      this.onLoadMore = (event) => {
        event.preventDefault();
        this.button.blur();
        this.loadProducts();
      };

      this.onProductClick = (event) => {
        const link = event.target.closest('a[href]');
        if (!link || !this.grid.contains(link)) return;

        const url = new URL(link.href, location.href);
        if (!url.pathname.includes('/products/')) return;

        const card = link.closest('.column');
        if (!card) return;

        this.saveState({
          productPath: url.pathname,
          offset: card.getBoundingClientRect().top,
          scrollY: window.scrollY
        });
      };

      this.onPageShow = (event) => {
        // BFCache 已经保留完整 DOM，不重新追加商品。
        if (event.persisted) {
          this.removeAttribute('loading');
          this.startObserver();
        }
      };

      this.grid.addEventListener('click', this.onProductClick, true);
      window.addEventListener('pageshow', this.onPageShow);

      if (
        this.button &&
        this.classList.contains('pagination-type--loadmore')
      ) {
        this.button.addEventListener('click', this.onLoadMore);
      }

      await this.restoreState();

      if (this.isConnected) this.startObserver();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.grid?.removeEventListener('click', this.onProductClick, true);
      this.button?.removeEventListener('click', this.onLoadMore);
      window.removeEventListener('pageshow', this.onPageShow);

      this.initialized = false;
    }

    readState() {
      try {
        return JSON.parse(sessionStorage.getItem(this.storageKey));
      } catch {
        return null;
      }
    }

    saveState(anchor) {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify({
          time: Date.now(),
          urls: this.loadedUrls,
          anchor
        }));
      } catch (error) {
        console.warn('Unable to save collection position:', error);
      }
    }

    startObserver() {
      if (
        !this.classList.contains('pagination-type--infinite') ||
        this.restoring ||
        this.exhausted
      ) return;

      this.observer?.disconnect();

      this.observer = new IntersectionObserver((entries) => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.loadProducts();
        }
      }, {
        threshold: 0,
        rootMargin: '100px'
      });

      this.observer.observe(this);
    }

    setExhausted() {
      this.exhausted = true;
      this.observer?.disconnect();
      this.button?.classList.add('visually-hidden');
    }

    async loadProducts(requestUrl, restoring = false) {
      if (
        this.hasAttribute('loading') ||
        this.exhausted ||
        (this.restoring && !restoring)
      ) return false;

      const url = new URL(requestUrl || location.href, location.href);

      if (!requestUrl) {
        url.searchParams.set('page', this.nextPage);
      }

      this.setAttribute('loading', 'true');

      try {
        const response = await fetch(url.href);
        if (!response.ok) {
          throw new Error(`Pagination HTTP ${response.status}`);
        }

        const html = await response.text();
        if (!this.isConnected) return false;

        const page = Number(url.searchParams.get('page')) || 1;
        const added = this.renderProducts(html, page, restoring);

        if (!added) {
          this.setExhausted();
          return false;
        }

        // 请求、渲染成功后才更新页码。
        this.nextPage = page + 1;

        if (!this.loadedUrls.includes(url.href)) {
          this.loadedUrls.push(url.href);
        }

        if (!restoring && typeof dispatchCustomEvent === 'function') {
          dispatchCustomEvent('pagination:page-changed', {
            url: url.pathname + url.search
          });
        }

        return true;
      } catch (error) {
        console.error('Unable to load collection products:', error);
        return false;
      } finally {
        this.removeAttribute('loading');
      }
    }

    renderProducts(html, page, restoring) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const sourceGrid = parsed.getElementById('product-grid');
      const products = sourceGrid
        ? [...sourceGrid.querySelectorAll('.column')]
        : [];

      if (!products.length) return false;

      // 恢复时直接追加，不使用 view transition 或入场动画。
      for (const product of products) {
        product.dataset.pagination = page;
        this.grid.appendChild(product);
      }

      products.forEach((product) => {
        product.querySelectorAll('.background-video__deferred')
          .forEach((template) => {
            const observer = new IntersectionObserver((entries) => {
              if (!entries.some(entry => entry.isIntersecting)) return;
              observer.disconnect();

              if (!template.isConnected) return;

              const clone = template.content.cloneNode(true);
              const video = clone.querySelector('video');

              template.replaceWith(clone);

              if (video?.poster) {
                const poster = video.poster;
                video.poster = '';
                video.poster = poster;
              }
            }, {
              rootMargin: '100px'
            });

            observer.observe(template.parentElement || template);
          });
      });

      if (this.animations_enabled) {
        const targets = products.map(product =>
          product.querySelector(
            '.product-card .product-featured-image-link, ' +
            '.products .gallery--item figure'
          )
        ).filter(Boolean);

        if (restoring) {
          gsap.set(targets, { scale: 1, opacity: 1 });
        } else {
          gsap.to(targets, {
            scale: 1,
            opacity: 1,
            stagger: 0.15,
            ease: window.theme?.settings?.animation_easing
          });
        }
      }

      const count = parsed.getElementById('ProductCount');

      if (count) {
        document.querySelectorAll('.thb-filter-count').forEach(item => {
          item.innerHTML = count.innerHTML;
        });
      }

      return true;
    }

    async restoreState() {
      const navigation = performance.getEntriesByType('navigation')[0];

      // 普通打开或刷新系列页，不自动加载旧的浏览记录。
      if (navigation?.type !== 'back_forward') return;

      const state = this.readState();

      if (
        !state?.anchor ||
        !Array.isArray(state.urls) ||
        !Number.isFinite(state.time) ||
        Date.now() - state.time > 30 * 60 * 1000
      ) return;

      this.restoring = true;
      const previousRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';

      try {
        for (const url of state.urls) {
          if (!this.isConnected) return;

          const success = await this.loadProducts(url, true);
          if (!success) break;
        }

        if (!this.isConnected) return;

        // 等待追加内容参与布局。
        await new Promise(resolve =>
          requestAnimationFrame(() => requestAnimationFrame(resolve))
        );

        const link = [...this.grid.querySelectorAll('a[href]')].find(
          item =>
            new URL(item.href, location.href).pathname ===
            state.anchor.productPath
        );

        const card = link?.closest('.column');

        const top = card
          ? window.scrollY +
            card.getBoundingClientRect().top -
            state.anchor.offset
          : state.anchor.scrollY;

        // 避免主题的 smooth scrolling 导致返回时缓慢滚动。
        const root = document.documentElement;
        const oldBehavior = root.style.scrollBehavior;

        root.style.scrollBehavior = 'auto';
        window.scrollTo({
          top: Math.max(0, top || 0),
          behavior: 'instant'
        });
        root.style.scrollBehavior = oldBehavior;
      } finally {
        this.restoring = false;
        history.scrollRestoration = previousRestoration;
      }
    }
  }

  customElements.define('pagination-theme', Pagination);
}
